import { Button, HStack, IconButton, Text, VStack } from 'native-base';
import { Control, FieldErrors } from 'react-hook-form';
import FormHeader from 'ui/components/FormBuilderV2/components/FormHeader';
import Icon from 'ui/components/Icon';
import Input from 'ui/components/Input';
import { colors } from 'ui/theme/colors';

import { ICreateEcosystemForm } from '~/components/EcosystemForm/types';

import { useDownloadableFilesFormController } from './controller';

interface DownloadableFilesFormProps {
  control: Control<ICreateEcosystemForm>;
  errors: FieldErrors<ICreateEcosystemForm>;
}

export const DownloadableFilesForm = ({
  control,
  errors,
}: DownloadableFilesFormProps) => {
  const { fields, append, remove } = useDownloadableFilesFormController({
    control,
  });

  return (
    <VStack space={4}>
      <FormHeader
        title="Downloadable Files"
        subTitle="Add files, documents or resources that will be available for download in your ecosystem"
      />
      {fields.map((field, index) => (
        <VStack
          key={field.id}
          space={3}
          p={4}
          borderWidth={1}
          borderRadius="md"
          borderColor="gray.200"
        >
          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="md" fontWeight="medium">
              File {index + 1}
            </Text>
            <IconButton
              icon={<Icon icon="trash" size="15" color={colors.error[600]} />}
              onPress={() => remove(index)}
              variant="ghost"
              colorScheme="red"
              size="sm"
            />
          </HStack>
          <Input
            name={`downloadableFiles.${index}.name`}
            control={control}
            placeholder="e.g. Project Guidelines PDF"
            label="File Name"
            helperText="Enter a descriptive name for the file that will be displayed to users"
            isRequired
            errorMessage={errors?.downloadableFiles?.[index]?.name?.message}
          />
          <Input
            name={`downloadableFiles.${index}.link`}
            control={control}
            placeholder="https://storage.example.com/my-file.pdf"
            label="File URL"
            helperText="Add a public URL where the file can be downloaded (e.g. Google Drive, Dropbox)"
            isRequired
            errorMessage={errors?.downloadableFiles?.[index]?.link?.message}
          />
          <Input
            name={`downloadableFiles.${index}.description`}
            control={control}
            placeholder="Brief description of what this file contains and its purpose"
            label="File Description"
            helperText="Provide a clear description to help users understand what they will find in this file"
            errorMessage={
              errors?.downloadableFiles?.[index]?.description?.message
            }
            multiline
            numberOfLines={3}
          />
        </VStack>
      ))}
      <Button
        leftIcon={
          <Icon icon="plus-circle" size="15" color={colors.primary[600]} />
        }
        onPress={() => append({ name: '', link: '', description: '' })}
        variant="outline"
      >
        Add New File
      </Button>
    </VStack>
  );
};
